import Feature from 'ol/Feature';
import type Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import type { EventsKey } from 'ol/events';
import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import Draw from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { unByKey } from 'ol/Observable';
import type { MapTool } from '../core/types';
import { formatAreaMeasurement, formatDistance, geodesicLength, measureArea } from '../domain/measurement';
import type { LiveMeasure } from './LiveMeasure';

type MeasureTool = Extract<MapTool, 'measure-distance' | 'measure-area'>;

export class MeasurementController {
  private readonly source = new VectorSource<Feature<Geometry>>();
  private readonly layer = new VectorLayer({
    source: this.source,
    style: new Style({ fill: new Fill({ color: 'rgba(255,190,52,.22)' }), stroke: new Stroke({ color: '#e39100', width: 3, lineDash: [8, 5] }) }),
    properties: { role: 'measurement' },
  });
  private draw: Draw | null = null;
  private liveOverlay: Overlay | null = null;
  private listener: EventsKey | null = null;
  private readonly overlays: Overlay[] = [];

  private disposeLive: (() => void) | null = null;

  constructor(private readonly map: Map, private readonly status: (text: string) => void, private readonly live: LiveMeasure | null = null) {
    this.layer.setZIndex(20_000);
    map.addLayer(this.layer);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.cancelActive(); });
  }

  setTool(tool: MapTool): void {
    this.removeInteraction();
    if (tool !== 'measure-distance' && tool !== 'measure-area') return;
    this.startInteraction(tool);
  }

  clear(): void {
    this.cancelActive();
    this.source.clear(true);
    this.overlays.forEach((overlay) => this.map.removeOverlay(overlay));
    this.overlays.length = 0;
    this.status('Sin medición');
  }

  private startInteraction(tool: MeasureTool): void {
    this.draw = new Draw({ source: this.source, type: tool === 'measure-distance' ? 'LineString' : 'Polygon', stopClick: true });
    this.map.addInteraction(this.draw);
    this.disposeLive = this.live?.attach(this.draw, { title: tool === 'measure-distance' ? 'Distancia' : 'Superficie' }) ?? null;
    this.draw.on('drawstart', ({ feature }) => {
      this.finishLiveOverlay();
      const element = document.createElement('div');
      element.className = 'measurement-tooltip live';
      this.liveOverlay = new Overlay({ element, offset: [10, -10], positioning: 'bottom-left' });
      this.map.addOverlay(this.liveOverlay);
      const geometry = feature.getGeometry();
      if (!geometry) return;
      this.listener = geometry.on('change', (event) => this.updateTooltip(event.target as Geometry, element));
      this.updateTooltip(geometry, element);
    });
    this.draw.on('drawend', ({ feature }) => {
      const geometry = feature.getGeometry();
      const element = this.liveOverlay?.getElement();
      if (geometry && element) this.updateTooltip(geometry, element);
      element?.classList.remove('live');
      element?.classList.add('final');
      this.finishLiveOverlay(true);
    });
  }

  private updateTooltip(geometry: Geometry, element: HTMLElement): void {
    if (geometry instanceof LineString) {
      const text = formatDistance(geodesicLength(geometry));
      element.textContent = text;
      this.liveOverlay?.setPosition(geometry.getLastCoordinate());
      this.status(`Distancia: ${text}`);
    } else if (geometry instanceof Polygon) {
      const text = formatAreaMeasurement(measureArea(geometry));
      element.textContent = text;
      this.liveOverlay?.setPosition(geometry.getInteriorPoint().getCoordinates());
      this.status(text);
    }
  }

  private cancelActive(): void {
    const hadActiveSketch = this.liveOverlay !== null;
    this.draw?.abortDrawing();
    this.finishLiveOverlay();
    if (hadActiveSketch) this.status('Medición cancelada');
  }

  private finishLiveOverlay(keep = false): void {
    if (this.listener) { unByKey(this.listener); this.listener = null; }
    if (this.liveOverlay) {
      if (keep) this.overlays.push(this.liveOverlay);
      else this.map.removeOverlay(this.liveOverlay);
      this.liveOverlay = null;
    }
  }

  private removeInteraction(): void {
    this.cancelActive();
    this.disposeLive?.(); this.disposeLive = null;
    if (this.draw) { this.map.removeInteraction(this.draw); this.draw = null; }
  }
}
